/**
 * GridCrash — Campaign Mode "The Climb" (6B)
 *
 * 30 levels ramping from a gentle 5×5 to a timed 16×16. Every level has
 * a fixed seed, so it is the same board on every attempt — a puzzle you
 * learn, not a dice roll. Progress (best stars per level) is offline-first
 * in localStorage, same pattern as quests.ts.
 *
 * Stars: 1★ win · 2★ also under par time · 3★ also no wrong flags and
 * no hints. First clear of a level pays a one-time coin reward.
 */

import { DIFFICULTIES, type DifficultyConfig } from "./engine.js";
import { creditBonus } from "./wallet.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CampaignLevel {
  /** 1-based level number. */
  num: number;
  rows: number;
  cols: number;
  mineCount: number;
  /** Countdown for timed levels (16+); undefined = untimed. */
  timeLimitSecs?: number;
  /** 2-star threshold: win at or under this many seconds. */
  parTimeSecs: number;
  /** One-time Clash Coin payout on first clear. */
  firstClearCoins: number;
  /** Fixed board seed — same board every attempt. */
  seed: number;
}

interface CampaignState {
  /** Best star count per level number (1–3; absent = never cleared). */
  stars: Record<number, number>;
}

export interface LevelOutcome {
  stars: 0 | 1 | 2 | 3;
  /** True when this run improved the stored best. */
  newBest: boolean;
  /** Coins paid out just now (0 unless this was the first clear). */
  coinsAwarded: number;
}

// ─── Level table ──────────────────────────────────────────────────────────────

export const CAMPAIGN_SIZE = 30;

/** First timed level — everything from here has a countdown. */
const FIRST_TIMED_LEVEL = 16;

function buildLevel(num: number): CampaignLevel {
  // Board ramps 5×5 (L1) → 16×16 (L30); density ramps ~10% → ~20%.
  const side = 5 + Math.floor(((num - 1) * 11) / (CAMPAIGN_SIZE - 1));
  const cells = side * side;
  const density = 0.10 + ((num - 1) * 0.10) / (CAMPAIGN_SIZE - 1);
  const mineCount = Math.max(2, Math.round(cells * density));

  // Par scales with open area; timed levels get double par on the clock.
  const parTimeSecs = Math.max(25, Math.round((cells - mineCount) * 1.1));
  const timeLimitSecs = num >= FIRST_TIMED_LEVEL ? parTimeSecs * 2 : undefined;

  return {
    num,
    rows: side,
    cols: side,
    mineCount,
    ...(timeLimitSecs !== undefined ? { timeLimitSecs } : {}),
    parTimeSecs,
    firstClearCoins: 15 + num * 5,
    // Knuth multiplicative hash keeps neighboring levels' boards unrelated.
    seed: ((num * 2654435761) >>> 0) || 1,
  };
}

export const CAMPAIGN_LEVELS: CampaignLevel[] = Array.from(
  { length: CAMPAIGN_SIZE },
  (_, i) => buildLevel(i + 1)
);

export function getCampaignLevel(num: number): CampaignLevel | null {
  return CAMPAIGN_LEVELS[num - 1] ?? null;
}

/**
 * Engine config for a level. The id maps to the closest standard tier so
 * coin rewards, achievements, and quests treat the level like that tier.
 */
export function toDifficultyConfig(level: CampaignLevel): DifficultyConfig {
  const cells = level.rows * level.cols;
  const tier = cells <= 81 ? DIFFICULTIES["easy"]
             : cells <= 169 ? DIFFICULTIES["medium"]
             : DIFFICULTIES["hard"];
  return {
    ...tier,
    label: `Level ${level.num}`,
    rows: level.rows,
    cols: level.cols,
    mineCount: level.mineCount,
    ...(level.timeLimitSecs !== undefined ? { timeLimitSecs: level.timeLimitSecs } : {}),
  };
}

// ─── Persistence ──────────────────────────────────────────────────────────────

const KEY = "gridcrash.campaign.v1";

let memState: CampaignState | null = null;

function readState(): CampaignState {
  if (memState) return memState;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CampaignState>;
      if (parsed.stars && typeof parsed.stars === "object") {
        memState = { stars: parsed.stars };
        return memState;
      }
    }
  } catch { /* corrupted or private browsing — start fresh */ }
  memState = { stars: {} };
  return memState;
}

function writeState(state: CampaignState): void {
  memState = state;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Best stars for a level (0 = never cleared). */
export function getLevelStars(num: number): number {
  return readState().stars[num] ?? 0;
}

/** A level is playable once the previous one has been cleared. */
export function isLevelUnlocked(num: number): boolean {
  if (num === 1) return true;
  return getLevelStars(num - 1) >= 1;
}

/** Lowest unlocked-but-uncleared level — where the map should scroll to. */
export function getCurrentLevel(): number {
  for (let n = 1; n <= CAMPAIGN_SIZE; n++) {
    if (getLevelStars(n) === 0) return n;
  }
  return CAMPAIGN_SIZE;
}

export function getTotalStars(): number {
  const { stars } = readState();
  return Object.values(stars).reduce((sum, s) => sum + s, 0);
}

/**
 * Feed a finished level attempt. Computes stars, persists the best,
 * and pays the one-time first-clear reward straight into the wallet.
 */
export function recordLevelResult(params: {
  levelNum: number;
  won: boolean;
  timeSecs: number;
  wrongFlags: number;
  hintsUsed: number;
}): LevelOutcome {
  const level = getCampaignLevel(params.levelNum);
  if (!level || !params.won) return { stars: 0, newBest: false, coinsAwarded: 0 };

  let stars: 1 | 2 | 3 = 1;
  if (params.timeSecs <= level.parTimeSecs) stars = 2;
  if (stars === 2 && params.wrongFlags === 0 && params.hintsUsed === 0) stars = 3;

  const state = readState();
  const prev = state.stars[level.num] ?? 0;
  const firstClear = prev === 0;
  const newBest = stars > prev;

  if (newBest) {
    writeState({ stars: { ...state.stars, [level.num]: stars } });
  }

  let coinsAwarded = 0;
  if (firstClear) {
    coinsAwarded = level.firstClearCoins;
    creditBonus(coinsAwarded);
  }

  return { stars, newBest, coinsAwarded };
}
